import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { CONTROL_STATE_OFF, CONTROL_STATE_ON, CONTROL_STATE_UNKNOWN, CONTROL_TYPE_ALARM } from '@apps-common/config/controls'
import { axios, ExtractFnReturnType, QueryConfig } from '@apps-common/lib'
import { IUnitControlWithStateModel } from '@apps-common/types/portal/unit-control'

export const QUERY_KEY_PORTAL_GET_UNIT_CONTROLS_LIST_WITH_STATE = (unitId?: string) =>
  ['getUnitControlListWithState', unitId].filter(Boolean)

const getUnitControlListWithState = async (unitId: string): Promise<IUnitControlWithStateModel[]> =>
  axios.get(`/unitcontrol/getcontrollistwithstate?unitId=${unitId}`).then((res) => res.data?.data)

type QueryFnType = typeof getUnitControlListWithState

type Props = {
  config?: QueryConfig<QueryFnType>
  unitId: string
}

export const useGetUnitControlListWithState = ({ config, unitId }: Props) => {
  const [updatedControls, setUpdatedControls] = useState<IUnitControlWithStateModel[]>([])

  const { isLoading, isFetching, isError, data, refetch } = useQuery<ExtractFnReturnType<QueryFnType>>({
    ...{ ...config, cacheTime: 0 },
    queryKey: QUERY_KEY_PORTAL_GET_UNIT_CONTROLS_LIST_WITH_STATE(unitId),
    queryFn: () => getUnitControlListWithState(unitId)
  })

  // alarm controls can share the same partition
  // this means that if one alarm control changes state it can affect others which share the same partition
  // the below func checks and updates alarm control states based on another which is updated
  // returns updated control states if a related partition is found
  const replaceStates = (existingControls: IUnitControlWithStateModel[] | undefined, newControls: IUnitControlWithStateModel[]) => {
    if (!existingControls || !newControls || newControls.length === 0) {
      return existingControls
    }

    const normalizeControlStates = (controlStates: unknown): string[] => {
      if (typeof controlStates === 'string') {
        return controlStates.split(',').filter(Boolean)
      }

      if (Array.isArray(controlStates)) {
        if (controlStates.every((s) => typeof s === 'string')) {
          return (controlStates as string[]).filter(Boolean)
        }

        return controlStates
          .map((s: any) => {
            if (!s) {
              return ''
            }

            if (typeof s === 'string') {
              return s
            }

            const name = s.name ?? s.stateName ?? ''
            const state = s.state ?? s.currentState ?? s.status ?? ''
            const partition = s.partition ?? s.alarmPartition ?? ''
            return `${name}:${state}:${partition}`
          })
          .filter(Boolean)
      }

      return []
    }

    const changedPartitions = newControls.reduce(
      (acc: ({ partition: string; state: string } | undefined)[], curr: IUnitControlWithStateModel) => {
        const splitStates = normalizeControlStates(curr?.controlStates)

        if (splitStates.length === 0) {
          return acc
        }

        const stateStates = splitStates
          .map((s) => {
            const sSplit = s.split(':')

            if (sSplit?.length === 3 && sSplit[2].toLowerCase() !== CONTROL_STATE_UNKNOWN) {
              return {
                state: sSplit[1],
                partition: sSplit[2]
              }
            }

            return undefined
          })
          .filter(Boolean)

        return [...acc, ...stateStates]
      },
      []
    )

    return existingControls.map((c) => {
      const splitStates = normalizeControlStates(c?.controlStates)

      if (c.controlTypeId === CONTROL_TYPE_ALARM && splitStates.length > 0) {

        const updatedStates = splitStates?.reduce(
          (acc: { name: string; partition: string | undefined; state: string | undefined }[], curr: string) => {
            const sSplit = curr.split(':')

            if (sSplit.length === 3) {
              const changes = changedPartitions
                .filter((cp) => cp?.partition === sSplit[2])
                .map((cp) => ({ name: sSplit[0], partition: cp?.partition, state: cp?.state }))

              return [...acc, ...changes]
            }

            return [...acc, { name: sSplit[0], partition: sSplit[1], state: CONTROL_STATE_UNKNOWN }]
          },
          []
        )

        if (updatedStates?.some((s) => s.state?.toLowerCase() === CONTROL_STATE_ON)) {
          return {
            ...c,
            currentState: CONTROL_STATE_ON,
            controlStates: updatedStates.map((s) => `${s.name}:${s.state}:${s.partition}`).join(',')
          }
        }

        return {
          ...c,
          currentState: CONTROL_STATE_OFF,
          controlStates: updatedStates.map((s) => `${s.name}:${s.state}:${s.partition}`).join(',')
        }
      }

      return c
    })
  }

  const updatedData = useMemo(() => {
    const controls = data?.map((d) => {
      const updated = updatedControls.find((c) => c.unitControlId === d.unitControlId)

      return updated || d
    })

    return replaceStates(controls, updatedControls)
  }, [data, updatedControls])

  const onControlStateUpdated = (control: IUnitControlWithStateModel) => {
    if (!control) return
    setUpdatedControls((prev) => [...prev.filter((c) => c.unitControlId !== control.unitControlId), control])
  }

  return { isLoading, isFetching, isError, data: updatedData, refetch, onControlStateUpdated }
}
